import React, { useState } from 'react';
import { Alert, AlertTitle, Box, Button } from '@mui/material';
import AutoFixHighOutlinedIcon from '@mui/icons-material/AutoFixHighOutlined';

const DISMISS_KEY = 'pithplay.news.quick-edits-2026-11';

// Browser storage is a convenience: private windows and blocked storage throw.
export const quickEditsNewsDismissed = (): boolean => {
  try {
    return window.localStorage.getItem(DISMISS_KEY) === 'dismissed';
  } catch {
    return false;
  }
};

interface QuickEditsNewsBannerProps {
  /** Opens a quick edit of the newest video (or the videos, when there are none). */
  onTry: () => void;
  onDismissed?: () => void;
}

/** Tells creators about quick edits once; closing it hides it in this browser. */
const QuickEditsNewsBanner: React.FC<QuickEditsNewsBannerProps> = ({ onTry, onDismissed }) => {
  const [dismissed, setDismissed] = useState(quickEditsNewsDismissed);
  if (dismissed) return null;
  const dismiss = () => {
    setDismissed(true);
    try {
      window.localStorage.setItem(DISMISS_KEY, 'dismissed');
    } catch {
      // Not remembered; it shows again next visit.
    }
    onDismissed?.();
  };
  return (
    <Alert severity="success" icon={<AutoFixHighOutlinedIcon fontSize="inherit" />} onClose={dismiss} closeText="Dismiss" sx={{ mb: 3 }} data-testid="quick-edits-news">
      <AlertTitle sx={{ fontWeight: 700 }}>New: quick edits</AlertTitle>
      Trim a video, add your logo, captions and music, reframe it for Shorts and export it yourself, in minutes. Or tell the assistant what to change. Exports come with Pro and Pro Max.
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
        <Button size="small" variant="outlined" color="inherit" onClick={onTry} data-testid="try-quick-edit">Try it on your newest video</Button>
      </Box>
    </Alert>
  );
};

export default QuickEditsNewsBanner;
